import * as _ from "lodash";
import { useState } from "react";
import { connect } from "react-redux";

import actions from "@/actions";
import { Form, Input, Modal, Select, Switch } from "@/components";
import type { Light } from "@/models/index";
import type MapSettings from "@/models/map-settings";

interface Catalog {
    beaconID: string;
    vlcId: string;
}

interface GeneralProps {
    lights: Light[];
    catalogs: Catalog[];
    selectBLE: (value: unknown) => void;
    checkCatalog: (vlc: unknown, uniqueId: unknown) => void;
    clearCatalogs: () => void;
    changeLights: (key: string, value: unknown) => void;
    persistLights: () => void;
    mapSettings: MapSettings;
}

const General = ({
    lights,
    catalogs,
    selectBLE,
    checkCatalog,
    clearCatalogs,
    changeLights,
    persistLights,
    mapSettings,
}: GeneralProps) => {
    const [vlc, setVlc] = useState(lights[0]?.id);
    const [bleId, setBleId] = useState(lights[0]?.bleId);
    const [bleSelectedOption, setBleSelectedOption] = useState("");

    const [prevLight, setPrevLight] = useState({
        vlc: lights[0]?.id,
        bleId: lights[0]?.bleId,
    });

    const positioningType =
        !_.isEmpty(mapSettings.fixtureDetails) &&
        mapSettings.fixtureDetails.positioningType;

    const currentLight = lights[0];
    if (!currentLight) return;

    if (currentLight.id !== prevLight.vlc) {
        setVlc(currentLight.id);
        setPrevLight((prev) => ({ ...prev, vlc: currentLight.id }));
    }

    if (currentLight.bleId !== prevLight.bleId) {
        setBleId(currentLight.bleId);
        setPrevLight((prev) => ({ ...prev, bleId: currentLight.bleId }));
    }

    const _getValue = (value, lightsArr, noValue = 0) => {
        const lightValue = _.get(lightsArr[0], value);
        const sameValue = _.reduce(lightsArr, (result, light) => {
            return lightValue === _.get(light, value);
        });
        return sameValue ? lightValue : noValue;
    };

    const renderBLEModal = () => {
        const modalProps = {
            title: "Multiple BLEs found",
            className: "floorspace-catalogs-modal",
            visible: catalogs.length > 0,
            onClose: clearCatalogs,
        };
        const formProps = {
            model: { ble: bleSelectedOption },
            onSubmit: () => selectBLE(bleSelectedOption),
            submitText: "Proceed",
            submitDisable: !bleSelectedOption,
        };
        const options = _.map(catalogs, (catalog) => ({
            label: catalog.beaconID,
            value: catalog,
        }));

        return (
            <Modal {...modalProps}>
                <Form {...formProps}>
                    <div className="row">
                        <Form.FormControl className="col" name="ble">
                            <label className="selectLabel">
                                What BLE would you like to associate with this
                                light:
                            </label>
                            <Select
                                searchable
                                options={options}
                                value={bleSelectedOption}
                                onChange={(option) =>
                                    setBleSelectedOption(
                                        option && "value" in option
                                            ? String(option.value)
                                            : "",
                                    )
                                }
                            />
                            <div className="info">
                                If you cancel VLCID &apos;
                                {catalogs.length > 0 && catalogs[0].vlcId}&apos;
                                will not be allowed for this light
                            </div>
                        </Form.FormControl>
                    </div>
                </Form>
            </Modal>
        );
    };

    const renderIdentifiers = (lightsArr) => {
        const disabled = lightsArr.length > 1;
        const getValue = (value) => (disabled ? "--" : value);
        switch (positioningType) {
            case "BLE":
                return (
                    <div className="group">
                        <div className="group-title">Identifiers</div>
                        <div className="row">
                            <div className="col">
                                <Input
                                    label="BLE ID"
                                    disabled={disabled}
                                    value={getValue(bleId)}
                                    onBlur={() =>
                                        selectBLE({
                                            beaconID: _.toString(bleId),
                                            vlcId: lightsArr[0].id,
                                        })
                                    }
                                    onChange={(value) =>
                                        setBleId(value ? value : "0")
                                    }
                                    type={!disabled ? "number" : "text"}
                                    integerOnly
                                />
                            </div>
                        </div>
                        <div className="col vlc-flag" />
                    </div>
                );
            case "VLC":
                return (
                    <div className="group">
                        <div className="group-title">Identifiers</div>
                        <div className="row">
                            <div className="col">
                                <Input
                                    label="VLC ID"
                                    disabled={disabled}
                                    value={getValue(vlc)}
                                    onBlur={() =>
                                        checkCatalog(vlc, lightsArr[0].uniqueId)
                                    }
                                    onChange={(value) => setVlc(Number(value))}
                                    type={!disabled ? "number" : "text"}
                                    integerOnly
                                />
                            </div>
                            <div className="col vlc-flag">
                                <Switch
                                    disabled={disabled}
                                    onChange={(value) =>
                                        changeLights("vlcIsActive", value)
                                    }
                                    checked={
                                        !disabled &&
                                        !_.isNil(lightsArr[0].vlcIsActive)
                                            ? lightsArr[0].vlcIsActive
                                            : true
                                    }
                                    style={{ marginRight: 5 }}
                                />
                            </div>
                        </div>
                    </div>
                );
            default:
                return (
                    <div className="group">
                        <div className="group-title">Identifiers</div>
                        <div className="row">
                            <div className="col">
                                <Input
                                    label="VLC ID"
                                    disabled={disabled}
                                    value={getValue(vlc)}
                                    onBlur={() =>
                                        checkCatalog(vlc, lightsArr[0].uniqueId)
                                    }
                                    onChange={(value) => setVlc(Number(value))}
                                    type={!disabled ? "number" : "text"}
                                    integerOnly
                                />
                            </div>
                            <div className="col vlc-flag">
                                <Switch
                                    disabled={disabled}
                                    onChange={(value) =>
                                        changeLights("vlcIsActive", value)
                                    }
                                    checked={
                                        !disabled &&
                                        !_.isNil(lightsArr[0].vlcIsActive)
                                            ? lightsArr[0].vlcIsActive
                                            : true
                                    }
                                    style={{ marginRight: 5 }}
                                />
                            </div>
                        </div>
                        <span className="ble-id">
                            BLE ID: {getValue(lightsArr[0].bleId)}
                        </span>
                        {renderBLEModal()}
                    </div>
                );
        }
    };

    const renderPosition = (lightsArr) => {
        const disabled = lightsArr.length > 1;
        const getValue = (value) => (disabled ? "--" : value);
        return [
            <div className="group" key="position">
                <div className="group-title">Position</div>
                <div className="row">
                    <div className="col">
                        <Input
                            label="X POS"
                            disabled={disabled}
                            value={getValue(lightsArr[0].position.x)}
                            onBlur={() => persistLights()}
                            onChange={(value) =>
                                changeLights("position", { x: value })
                            }
                            type={!disabled ? "number" : "text"}
                            unit="ft"
                            scientificNotation={false}
                        />
                    </div>
                    <div className="col">
                        <Input
                            label="Y POS"
                            disabled={disabled}
                            value={getValue(lightsArr[0].position.y)}
                            onBlur={() => persistLights()}
                            onChange={(value) =>
                                changeLights("position", { y: value })
                            }
                            type={!disabled ? "number" : "text"}
                            unit="ft"
                            scientificNotation={false}
                        />
                    </div>
                    <div className="col">
                        <Input
                            label="Z POS"
                            value={_getValue("position.z", lightsArr)}
                            onBlur={() => persistLights()}
                            onChange={(value) =>
                                changeLights("position", { z: value })
                            }
                            type="number"
                            unit="ft"
                            scientificNotation={false}
                        />
                    </div>
                </div>
            </div>,
            <div className="group" key="location-description">
                <div className="row">
                    <div className="col">
                        <Input
                            label="Location Description"
                            disabled={disabled}
                            value={getValue(lightsArr[0].locationDescription)}
                            onBlur={() => persistLights()}
                            onChange={(value) =>
                                changeLights("locationDescription", value)
                            }
                        />
                    </div>
                </div>
            </div>,
        ];
    };

    const renderSignalAperture = (lightsArr) => {
        return (
            <div className="group">
                <div className="group-title">Signal Aperture</div>
                <div className="row">
                    <div className="col">
                        <Input
                            label="Width"
                            value={_getValue("signalAperture.width", lightsArr)}
                            onChange={(value) =>
                                changeLights("signalAperture", { width: value })
                            }
                            onBlur={() => persistLights()}
                            type="number"
                            unit="ft"
                            scientificNotation={false}
                        />
                    </div>
                    <div className="col">
                        <Input
                            label="Length"
                            value={_getValue(
                                "signalAperture.length",
                                lightsArr,
                            )}
                            onChange={(value) =>
                                changeLights("signalAperture", {
                                    length: value,
                                })
                            }
                            onBlur={() => persistLights()}
                            type="number"
                            unit="ft"
                            scientificNotation={false}
                        />
                    </div>
                </div>
            </div>
        );
    };

    const renderExternalDimensions = (lightsArr) => {
        return (
            <div className="group">
                <div className="group-title">External Dimensions</div>
                <div className="row">
                    <div className="col">
                        <Input
                            label="Width"
                            value={_getValue(
                                "externalDimension.width",
                                lightsArr,
                            )}
                            onChange={(value) =>
                                changeLights("externalDimension", {
                                    width: value,
                                })
                            }
                            onBlur={() => persistLights()}
                            type="number"
                            unit="ft"
                            scientificNotation={false}
                        />
                    </div>
                    <div className="col">
                        <Input
                            label="Length"
                            value={_getValue(
                                "externalDimension.length",
                                lightsArr,
                            )}
                            onChange={(value) =>
                                changeLights("externalDimension", {
                                    length: value,
                                })
                            }
                            onBlur={() => persistLights()}
                            type="number"
                            unit="ft"
                            scientificNotation={false}
                        />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="col tab general">
            {renderIdentifiers(lights)}
            {renderPosition(lights)}
            {renderSignalAperture(lights)}
            {renderExternalDimensions(lights)}
        </div>
    );
};

const mapStateToProps = ({ map }) => ({
    catalogs: map.general.floorspaceCatalogs,
    mapSettings: map.general.settings,
});

const mapDispatchToProps = {
    selectBLE: actions.map.general.selectBLE,
    checkCatalog: actions.map.general.checkFloorspaceCatalogs,
    clearCatalogs: () => actions.map.general.setFloorspaceCatalogs([]),
};

export default connect(mapStateToProps, mapDispatchToProps)(General);
